import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { supabase } from '../../lib/supabase';

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If already logged in as super admin, redirect straight to dashboard
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const ownerEmail = import.meta.env?.VITE_SUPER_ADMIN_EMAIL;
          const isAdminByEmail = ownerEmail && user?.email?.toLowerCase() === ownerEmail?.toLowerCase();

          // Check admin_users table
          const { data: adminRecord } = await supabase
            ?.from('admin_users')
            ?.select('id, is_active')
            ?.eq('id', user?.id)
            ?.single();

          if (isAdminByEmail || adminRecord?.is_active) {
            navigate('/super-admin-dashboard');
          }
        }
      } catch {
        // No session or error — stay on login page
      }
    };
    checkExistingSession();
  }, [navigate]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Authenticate with Supabase
      const { data, error: authError } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(authError?.message || 'Invalid email or password.');
        return;
      }

      if (!data?.user) {
        setError('Authentication failed. Please try again.');
        return;
      }

      // Step 2: Verify this user has an active admin_users record.
      // Email-match alone is not sufficient — the database record is the
      // authoritative source of admin status after RLS hardening.
      let isAdminByRecord = false;
      try {
        const { data: adminRecord } = await supabase
          ?.from('admin_users')
          ?.select('id, is_active, role')
          ?.eq('id', data?.user?.id)
          ?.single();
        isAdminByRecord = adminRecord?.is_active === true;
      } catch {
        // Query failed (e.g. RLS denied access) — treat as not-admin
      }

      if (!isAdminByRecord) {
        // Sign out the user since they don't have an active admin record
        await supabase?.auth?.signOut();
        setError('Access denied. This account does not have Super Admin privileges. Use the Admin Onboarding wizard to set up your admin account.');
        return;
      }

      // Step 5: Update last_login_at if record exists
      if (isAdminByRecord) {
        await supabase
          ?.from('admin_users')
          ?.update({ last_login_at: new Date()?.toISOString() })
          ?.eq('id', data?.user?.id);
      }

      navigate('/super-admin-dashboard');
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Super Admin Login - WebForms</title>
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-md">
          {/* Card */}
          <div className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 px-8 py-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center border border-white/20">
                  <Icon name="ShieldCheck" size={32} color="white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">Super Admin</h1>
              <p className="text-gray-400 text-sm">Restricted access — authorized personnel only</p>
            </div>

            {/* Form */}
            <div className="px-8 py-8">
              {error && (
                <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                  <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0" color="#b91c1c" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Admin Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      required
                      placeholder="admin@example.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Lock" size={16} color="var(--color-muted-foreground)" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      required
                      placeholder="Enter your password"
                      className="w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
                >
                  {loading ? (
                    <>
                      <Icon name="Loader2" size={16} color="white" className="animate-spin" />
                      Authenticating…
                    </>
                  ) : (
                    <>
                      <Icon name="LogIn" size={16} color="white" />
                      Sign In to Super Admin
                    </>
                  )}
                </button>
              </form>

              {/* Back link */}
              <div className="mt-6 text-center space-y-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
                >
                  <Icon name="ArrowLeft" size={14} color="currentColor" />
                  Back to Dashboard
                </button>
                <div className="border-t border-border pt-3">
                  <button
                    onClick={() => navigate('/admin-onboarding')}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
                  >
                    <Icon name="Sparkles" size={14} color="currentColor" />
                    First time? Run the setup wizard
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Logo below card */}
          <div className="mt-6 flex justify-center">
            <Image
              src="/assets/images/web-forms-logo-1769801123836.png"
              alt="WebForms.biz logo"
              className="h-7 w-auto opacity-60"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminLogin;
